import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  CloudUpload,
  Delete,
  Edit,
  Favorite,
  LockOutlined,
} from "@mui/icons-material";
import {
  HealthAccessLevel,
  HealthCheckType,
  HealthDocument,
  HealthDocumentType,
  HealthFitStatus,
  HealthRecordStatus,
  SheqHealthRecord,
  useCreateSheqHealthRecordMutation,
  useDeleteSheqHealthRecordMutation,
  useGetSheqHealthRecordsQuery,
  useUpdateSheqHealthRecordMutation,
} from "../../../redux/sheqApi";
import { usePostGetSignedUrlMutation } from "../../../redux/vmsApi";
import { useGetComsBlocksQuery } from "../../../redux/comsApi";
import { useGetLabourWorkersQuery } from "../../../redux/labourTrackerApi";
import { uploadFileToS3 } from "../../../utils/mediaUtil";

const NAVY = "#123456";

const CHECK_TYPES: { value: HealthCheckType; label: string }[] = [
  { value: "PRE_EMPLOYMENT", label: "Pre-employment" },
  { value: "PERIODIC", label: "Periodic" },
  { value: "RETURN_TO_WORK", label: "Return to Work" },
  { value: "POST_INCIDENT", label: "Post-incident" },
  { value: "EXIT", label: "Exit" },
];

const FIT_STATUSES: HealthFitStatus[] = [
  "FIT",
  "FIT_WITH_RESTRICTIONS",
  "TEMP_UNFIT",
  "UNFIT",
  "REFERRED",
];

const ACCESS_LEVELS: HealthAccessLevel[] = ["RESTRICTED", "CONFIDENTIAL"];
const RECORD_STATUSES: HealthRecordStatus[] = ["ACTIVE", "ARCHIVED", "REDACTED"];
const DOCUMENT_TYPES: { value: HealthDocumentType; label: string }[] = [
  { value: "MEDICAL_CERTIFICATE", label: "Medical Certificate" },
  { value: "XRAY", label: "X-Ray" },
  { value: "SCAN", label: "Scan" },
  { value: "LAB_RESULTS", label: "Lab Results" },
  { value: "PRESCRIPTION", label: "Prescription" },
  { value: "FIT_NOTE", label: "Fit Note" },
  { value: "OTHER", label: "Other" },
];

const FIT_COLOR: Record<HealthFitStatus, string> = {
  FIT: "#2e7d32",
  FIT_WITH_RESTRICTIONS: "#ef6c00",
  TEMP_UNFIT: "#c62828",
  UNFIT: "#8e0000",
  REFERRED: "#1565c0",
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function parseSupportingDocuments(raw?: string): HealthDocument[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as HealthDocument[];
    return Array.isArray(parsed)
      ? parsed.filter(
          (d) =>
            d &&
            typeof d.documentType === "string" &&
            typeof d.name === "string" &&
            typeof d.url === "string",
        )
      : [];
  } catch {
    return [];
  }
}

interface HealthDialogProps {
  open: boolean;
  record: SheqHealthRecord | null;
  onClose: () => void;
}

const emptyHealthRecord = (): Partial<SheqHealthRecord> => ({
  checkDate: today(),
  checkType: "PERIODIC",
  recordType: "HEALTH_CHECK",
  fitStatus: "FIT",
  followUpRequired: false,
  consentToStoreMedicalData: false,
  accessLevel: "RESTRICTED",
  recordStatus: "ACTIVE",
});

const HealthDialog: React.FC<HealthDialogProps> = ({ open, record, onClose }) => {
  const { data: workers = [] } = useGetLabourWorkersQuery({ active: true });
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const [create, { isLoading: creating }] = useCreateSheqHealthRecordMutation();
  const [update, { isLoading: updating }] = useUpdateSheqHealthRecordMutation();
  const [getSignedUrl] = usePostGetSignedUrlMutation();

  const [form, setForm] = useState<Partial<SheqHealthRecord>>(record ?? emptyHealthRecord());
  const [documentType, setDocumentType] = useState<HealthDocumentType>("MEDICAL_CERTIFICATE");
  const [documents, setDocuments] = useState<HealthDocument[]>(
    parseSupportingDocuments(record?.supportingDocuments),
  );
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string>("");

  React.useEffect(() => {
    setForm(record ?? emptyHealthRecord());
    setDocumentType("MEDICAL_CERTIFICATE");
    setDocuments(parseSupportingDocuments(record?.supportingDocuments));
    setUploadProgress(null);
    setUploadError("");
  }, [record, open]);

  const editing = !!record;
  const saving = creating || updating;

  const set = (key: keyof SheqHealthRecord, val: unknown) => {
    setForm((f) => ({ ...f, [key]: val }));
  };

  const requiresRestriction =
    form.fitStatus === "FIT_WITH_RESTRICTIONS" ||
    form.fitStatus === "TEMP_UNFIT" ||
    form.fitStatus === "UNFIT";

  const canSave =
    !!form.workerId &&
    !!form.checkDate &&
    !!form.checkType &&
    !!form.fitStatus &&
    !!form.consentToStoreMedicalData &&
    (!(form.followUpRequired ?? false) || !!form.followUpByDate) &&
    (!requiresRestriction || !!form.workRestrictions);

  const handleSave = async () => {
    if (!canSave) return;

    const worker = workers.find((w) => w.id === form.workerId);
    const block = blocks.find((b) => b.id === form.blockId);

    const payload = {
      ...form,
      workerName: worker?.name ?? form.workerName ?? "",
      workerEmployeeNo: worker?.nationalId ?? form.workerEmployeeNo,
      blockName: block?.name ?? form.blockName,
      followUpRequired: form.followUpRequired ?? false,
      consentToStoreMedicalData: form.consentToStoreMedicalData ?? false,
      accessLevel: form.accessLevel ?? "RESTRICTED",
      recordStatus: form.recordStatus ?? "ACTIVE",
      recordType: form.recordType ?? "HEALTH_CHECK",
      supportingDocuments: JSON.stringify(documents),
    } as Omit<SheqHealthRecord, "id" | "createdAt" | "updatedAt">;

    try {
      if (editing && record?.id) {
        await update({ id: record.id, body: payload }).unwrap();
      } else {
        await create(payload).unwrap();
      }
      onClose();
    } catch {
      // handled by global API notification middleware
    }
  };

  const handleUploadDocument = async (file?: File) => {
    if (!file) return;
    setUploadError("");
    setUploadProgress(0);
    try {
      const uploaded = await uploadFileToS3(
        file,
        getSignedUrl,
        setUploadProgress,
        true,
        "sheq-health",
      );
      setDocuments((prev) => [
        ...prev,
        {
          documentType,
          name: uploaded.name,
          url: uploaded.url,
          uploadedAt: new Date().toISOString(),
        },
      ]);
      setUploadProgress(null);
    } catch {
      setUploadProgress(null);
      setUploadError("Upload failed. Please try again.");
    }
  };

  const removeDocument = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? "Edit Health Record" : "Log Health Check"}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Alert severity="info" icon={<LockOutlined />}>
            HIPAA-aligned capture: only store safety-relevant health details, record explicit consent,
            and keep access restricted to authorized personnel.
          </Alert>

          <Typography variant="caption" fontWeight={700} color="text.secondary">
            PERSON AND CHECK DETAILS
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <FormControl size="small" sx={{ flex: "1 1 220px" }} required>
              <InputLabel>Staff Member</InputLabel>
              <Select
                label="Staff Member"
                value={form.workerId ?? ""}
                onChange={(e) => set("workerId", e.target.value)}
              >
                {workers.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: "1 1 180px" }}>
              <InputLabel>Site / Block</InputLabel>
              <Select
                label="Site / Block"
                value={form.blockId ?? ""}
                onChange={(e) => set("blockId", e.target.value)}
              >
                <MenuItem value="">-</MenuItem>
                {blocks.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Department"
              size="small"
              value={form.department ?? ""}
              onChange={(e) => set("department", e.target.value)}
              sx={{ flex: "1 1 160px" }}
            />
          </Box>

          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Check Date"
              type="date"
              size="small"
              required
              value={form.checkDate ?? ""}
              onChange={(e) => set("checkDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: "1 1 170px" }}
            />
            <FormControl size="small" sx={{ flex: "1 1 200px" }} required>
              <InputLabel>Check Type</InputLabel>
              <Select
                label="Check Type"
                value={form.checkType ?? "PERIODIC"}
                onChange={(e) => set("checkType", e.target.value)}
              >
                {CHECK_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: "1 1 180px" }} required>
              <InputLabel>Fitness Outcome</InputLabel>
              <Select
                label="Fitness Outcome"
                value={form.fitStatus ?? "FIT"}
                onChange={(e) => set("fitStatus", e.target.value)}
              >
                {FIT_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Provider Name"
              size="small"
              value={form.providerName ?? ""}
              onChange={(e) => set("providerName", e.target.value)}
              sx={{ flex: "1 1 200px" }}
            />
            <TextField
              label="Provider Registration Number"
              size="small"
              value={form.providerRegistrationNo ?? ""}
              onChange={(e) => set("providerRegistrationNo", e.target.value)}
              sx={{ flex: "1 1 220px" }}
            />
            <TextField
              label="Next Review Date"
              type="date"
              size="small"
              value={form.nextReviewDate ?? ""}
              onChange={(e) => set("nextReviewDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: "1 1 180px" }}
            />
          </Box>

          <Typography variant="caption" fontWeight={700} color="text.secondary">
            SAFETY-CRITICAL HEALTH NOTES (MINIMUM NECESSARY)
          </Typography>
          <TextField
            label="Work Restrictions (required for restricted/unfit outcomes)"
            size="small"
            multiline
            rows={2}
            required={requiresRestriction}
            value={form.workRestrictions ?? ""}
            onChange={(e) => set("workRestrictions", e.target.value)}
          />
          <TextField
            label="Allergies relevant to workplace response"
            size="small"
            value={form.allergies ?? ""}
            onChange={(e) => set("allergies", e.target.value)}
          />
          <TextField
            label="Chronic conditions relevant to safety"
            size="small"
            value={form.chronicConditions ?? ""}
            onChange={(e) => set("chronicConditions", e.target.value)}
          />
          <TextField
            label="Current medication affecting task safety"
            size="small"
            value={form.currentMedication ?? ""}
            onChange={(e) => set("currentMedication", e.target.value)}
          />
          <TextField
            label="Immunizations / surveillance notes"
            size="small"
            value={form.immunizations ?? ""}
            onChange={(e) => set("immunizations", e.target.value)}
          />
          <TextField
            label="Occupational exposure notes"
            size="small"
            multiline
            rows={2}
            value={form.exposureNotes ?? ""}
            onChange={(e) => set("exposureNotes", e.target.value)}
          />

          <Typography variant="caption" fontWeight={700} color="text.secondary">
            SUPPORTING MEDICAL DOCUMENTS
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Document Type</InputLabel>
              <Select
                label="Document Type"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value as HealthDocumentType)}
              >
                {DOCUMENT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUpload />}
              sx={{ borderColor: NAVY, color: NAVY }}
            >
              Upload File
              <input
                hidden
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  handleUploadDocument(file);
                  e.currentTarget.value = "";
                }}
              />
            </Button>

            {uploadProgress !== null && (
              <Typography variant="caption" color="text.secondary">
                Uploading: {uploadProgress}%
              </Typography>
            )}
          </Box>
          {uploadError && <Alert severity="error">{uploadError}</Alert>}
          {documents.length > 0 && (
            <Box display="flex" flexDirection="column" gap={1}>
              {documents.map((doc, index) => (
                <Box
                  key={`${doc.name}-${index}`}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 1 }}
                >
                  <Box minWidth={0}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {doc.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {doc.documentType} | {new Date(doc.uploadedAt).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" display="block">
                      <a href={doc.url} target="_blank" rel="noreferrer">
                        Open document
                      </a>
                    </Typography>
                  </Box>
                  <IconButton size="small" color="error" onClick={() => removeDocument(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={form.followUpRequired ?? false}
                  onChange={(e) => set("followUpRequired", e.target.checked)}
                />
              }
              label="Follow-up required"
            />
            {(form.followUpRequired ?? false) && (
              <TextField
                label="Follow-up by"
                type="date"
                size="small"
                value={form.followUpByDate ?? ""}
                onChange={(e) => set("followUpByDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>

          <Typography variant="caption" fontWeight={700} color="text.secondary">
            EMERGENCY CONTACT
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Contact Name"
              size="small"
              value={form.emergencyContactName ?? ""}
              onChange={(e) => set("emergencyContactName", e.target.value)}
              sx={{ flex: "1 1 180px" }}
            />
            <TextField
              label="Relationship"
              size="small"
              value={form.emergencyContactRelationship ?? ""}
              onChange={(e) => set("emergencyContactRelationship", e.target.value)}
              sx={{ flex: "1 1 160px" }}
            />
            <TextField
              label="Phone"
              size="small"
              value={form.emergencyContactPhone ?? ""}
              onChange={(e) => set("emergencyContactPhone", e.target.value)}
              sx={{ flex: "1 1 160px" }}
            />
          </Box>

          <Typography variant="caption" fontWeight={700} color="text.secondary">
            PRIVACY AND GOVERNANCE
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={form.consentToStoreMedicalData ?? false}
                onChange={(e) => set("consentToStoreMedicalData", e.target.checked)}
              />
            }
            label="Explicit consent to store personal health data (required)"
          />
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Consent Captured By"
              size="small"
              value={form.consentCapturedBy ?? ""}
              onChange={(e) => set("consentCapturedBy", e.target.value)}
              sx={{ flex: "1 1 180px" }}
            />
            <TextField
              label="Consent Captured At"
              type="datetime-local"
              size="small"
              value={form.consentCapturedAt ?? ""}
              onChange={(e) => set("consentCapturedAt", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: "1 1 220px" }}
            />
            <TextField
              label="Retention Until"
              type="date"
              size="small"
              value={form.retentionUntil ?? ""}
              onChange={(e) => set("retentionUntil", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: "1 1 170px" }}
            />
          </Box>

          <Box display="flex" gap={2} flexWrap="wrap">
            <FormControl size="small" sx={{ flex: "1 1 180px" }}>
              <InputLabel>Access Level</InputLabel>
              <Select
                label="Access Level"
                value={form.accessLevel ?? "RESTRICTED"}
                onChange={(e) => set("accessLevel", e.target.value)}
              >
                {ACCESS_LEVELS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: "1 1 180px" }}>
              <InputLabel>Record Status</InputLabel>
              <Select
                label="Record Status"
                value={form.recordStatus ?? "ACTIVE"}
                onChange={(e) => set("recordStatus", e.target.value)}
              >
                {RECORD_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: "1 1 180px" }}>
              <InputLabel>Record Type</InputLabel>
              <Select
                label="Record Type"
                value={form.recordType ?? "HEALTH_CHECK"}
                onChange={(e) => set("recordType", e.target.value)}
              >
                <MenuItem value="HEALTH_CHECK">HEALTH_CHECK</MenuItem>
                <MenuItem value="HEALTH_RECORD_UPDATE">HEALTH_RECORD_UPDATE</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Authorized disclosures (if any)"
            size="small"
            multiline
            rows={2}
            value={form.authorizedDisclosures ?? ""}
            onChange={(e) => set("authorizedDisclosures", e.target.value)}
          />
          <TextField
            label="Administrative notes"
            size="small"
            multiline
            rows={2}
            value={form.notes ?? ""}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || saving}
          sx={{ bgcolor: NAVY }}
        >
          {saving ? <CircularProgress size={16} /> : editing ? "Save" : "Create Record"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface Props {
  blockId?: string;
}

export const SheqHealth: React.FC<Props> = ({ blockId }) => {
  const [checkTypeFilter, setCheckTypeFilter] = useState<string>("");
  const [fitStatusFilter, setFitStatusFilter] = useState<string>("");
  const [documentTypeFilter, setDocumentTypeFilter] =
    useState<HealthDocumentType | "">("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [followUpOnly, setFollowUpOnly] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<SheqHealthRecord | null>(null);

  const { data: records = [], isLoading } = useGetSheqHealthRecordsQuery({
    blockId: blockId || undefined,
    checkType: checkTypeFilter || undefined,
    fitStatus: fitStatusFilter || undefined,
    from: from || undefined,
    to: to || undefined,
    followUpRequired: followUpOnly ? true : undefined,
  });

  const [removeRecord, { isLoading: deleting }] = useDeleteSheqHealthRecordMutation();

  const sorted = useMemo(() => {
    const filtered = documentTypeFilter
      ? records.filter((r) =>
          parseSupportingDocuments(r.supportingDocuments).some(
            (d) => d.documentType === documentTypeFilter,
          ),
        )
      : records;

    return [...filtered].sort((a, b) => b.checkDate.localeCompare(a.checkDate));
  }, [records, documentTypeFilter]);

  const openCreate = () => {
    setEditRecord(null);
    setDialogOpen(true);
  };

  const openEdit = (row: SheqHealthRecord) => {
    setEditRecord(row);
    setDialogOpen(true);
  };

  const onDelete = async (id?: string) => {
    if (!id) return;
    if (!window.confirm("Delete this health record?")) return;
    try {
      await removeRecord(id).unwrap();
    } catch {
      // handled globally
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Favorite sx={{ color: NAVY }} />
          <Typography variant="h4" sx={{ color: NAVY, fontWeight: 700 }}>
            Health
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ bgcolor: NAVY }}>
          Add Health Record
        </Button>
      </Box>

      <Alert severity="warning" sx={{ mb: 2 }}>
        This register stores personal health data. Apply minimum-necessary capture, restricted access,
        documented consent, and retention controls for HIPAA-aligned handling.
      </Alert>

      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Check Type</InputLabel>
            <Select
              label="Check Type"
              value={checkTypeFilter}
              onChange={(e) => setCheckTypeFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {CHECK_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Fitness Status</InputLabel>
            <Select
              label="Fitness Status"
              value={fitStatusFilter}
              onChange={(e) => setFitStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {FIT_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Document Type</InputLabel>
            <Select
              label="Document Type"
              value={documentTypeFilter}
              onChange={(e) =>
                setDocumentTypeFilter(e.target.value as HealthDocumentType | "")
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="MEDICAL_CERTIFICATE">Medical Certificate</MenuItem>
              <MenuItem value="XRAY">X-Ray</MenuItem>
              {DOCUMENT_TYPES.filter(
                (d) => d.value !== "MEDICAL_CERTIFICATE" && d.value !== "XRAY",
              ).map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            control={<Switch checked={followUpOnly} onChange={(e) => setFollowUpOnly(e.target.checked)} />}
            label="Follow-up only"
          />
        </Box>
      </Card>

      <Card variant="outlined">
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Staff</TableCell>
                  <TableCell>Check Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Fitness</TableCell>
                  <TableCell>Documents</TableCell>
                  <TableCell>Follow-up</TableCell>
                  <TableCell>Next Review</TableCell>
                  <TableCell>Consent</TableCell>
                  <TableCell>Access</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((r) => (
                  <TableRow key={r.id} hover>
                    {(() => {
                      const rowDocs = parseSupportingDocuments(r.supportingDocuments);
                      return (
                        <>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {r.workerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.workerEmployeeNo || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.checkDate}</TableCell>
                    <TableCell>{r.checkType}</TableCell>
                    <TableCell>
                      <Chip
                        label={r.fitStatus}
                        size="small"
                        sx={{ bgcolor: FIT_COLOR[r.fitStatus], color: "#fff", fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${rowDocs.length} file${rowDocs.length === 1 ? "" : "s"}`}
                        size="small"
                        color={rowDocs.length > 0 ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      {r.followUpRequired ? (
                        <Chip label={r.followUpByDate || "Required"} size="small" color="warning" />
                      ) : (
                        <Chip label="No" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{r.nextReviewDate || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={r.consentToStoreMedicalData ? "Yes" : "No"}
                        size="small"
                        color={r.consentToStoreMedicalData ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell>{r.accessLevel}</TableCell>
                    <TableCell>{r.recordStatus}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(r)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => onDelete(r.id)} disabled={deleting}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                        </>
                      );
                    })()}
                  </TableRow>
                ))}
                {sorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Box py={4} textAlign="center">
                        <Typography variant="body2" color="text.secondary">
                          No health records found for the selected filters.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <HealthDialog open={dialogOpen} record={editRecord} onClose={() => setDialogOpen(false)} />
    </Box>
  );
};
